import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card as CardBase, Flex } from 'rebass';
import styled from 'styled-components';
import { Typography, AssigneeList, Divider } from '@oca/ui';
import { GridInterface, CalendarEdit, WorldNetwork } from '@oca/icons';
import dayjs from 'dayjs';

export const Card = React.forwardRef(
  /**
   *
   * @param {CardProps} props
   * @param {React.Ref} ref
   */
  function Card(props, ref) {
    const {
      endTime,
      formatStr,
      location,
      project,
      startTime,
      status,
      title,
      assignees,
      date,
      ...rest
    } = props;
    const range = [new Date(startTime), new Date(endTime)]
      .map(d => dayjs(d).format(formatStr))
      .join(' - ');

    return (
      <CardContainer {...rest} px={3} pb={2} bg="greys.1" ref={ref}>
        <Flex justifyContent="space-between" alignItems="center">
          <Box width={100} paddingLeft={18}>
            {date ? (
              <Flex
                flexDirection="column"
                alignItems="start"
                justifyContent="center"
                textAlign="center"
              >
                <Typography color="primary.main" variant="h2" marginBottom={1}>
                  {dayjs(date).format('ddd')}
                </Typography>
                <Typography
                  color="primary.main"
                  fontSize={40}
                  fontWeight={500}
                  variant="h1"
                  marginBottom={1}
                >
                  {dayjs(date).format('D')}
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {dayjs(date).format('MMM')}
                </Typography>
              </Flex>
            ) : (
              <Typography variant="h2">---</Typography>
            )}
          </Box>
          <Box
            width={2}
            backgroundColor="stroke"
            alignSelf="stretch"
            marginLeft={2}
            marginRight={20}
          />
          <StatusIndicator status={status} />
          <Box flex={1}>
            <Typography
              fontWeight={500}
              title={title}
              fontSize={18}
              noWrap
              marginBottom={2}
            >
              {title}
            </Typography>
            <Flex alignItems="center" marginBottom={3} style={{ gap: 8 }}>
              <GridInterface />
              <Typography
                title={project}
                fontSize={14}
                noWrap
                color="textSecondary"
              >
                {' '}
                {project}
              </Typography>
            </Flex>
          </Box>
          <Box flex={1}>
            <Flex alignItems="center" marginBottom={3} style={{ gap: 8 }}>
              <CalendarEdit />
              <Typography color="textSecondary">{range}</Typography>
            </Flex>
            <Flex alignItems="center" style={{ gap: 8 }}>
              <WorldNetwork />
              <Typography title={location} fontSize={14} noWrap>
                <Typography as="span" color="textSecondary">
                  in
                </Typography>{' '}
                {location}
              </Typography>
            </Flex>
          </Box>
          <Box width={120}>
            <AssigneeList assignees={assignees} />
          </Box>
        </Flex>
      </CardContainer>
    );
  },
);

Card.propTypes = {
  date: PropTypes.date,
  endTime: PropTypes.string,
  formatStr: PropTypes.string,
  location: PropTypes.string,
  project: PropTypes.string,
  startTime: PropTypes.string,
  status: PropTypes.oneOf(['upcoming', 'completed']),
  title: PropTypes.string,
  assignees: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
    }),
  ),
};

Card.defaultProps = {
  date: null,
  endTime: null,
  formatStr: 'LT',
  location: null,
  project: null,
  startTime: null,
  status: 'upcoming',
  title: '',
  assignees: [],
};

const CardContainer = styled(CardBase).attrs(({ onClick }) => ({
  'data-clickable': !!onClick,
}))`
  display: block;
  padding: 16px 18px 16px 32px;
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 12px;
  text-decoration: none;
  &[data-clickable='true'] {
    cursor: pointer;
  }
`;

const StatusIndicator = styled.div.attrs(p => ({ 'data-status': p.status }))`
  position: absolute;
  left: 0;
  top: 30px;
  width: 4px;
  height: 24px;
  &[data-status='upcoming'] {
    background-color: ${p => p.theme.colors.danger};
  }
  &[data-status='completed'] {
    background-color: ${p => p.theme.colors.success};
  }
  &[data-status='cancelled'] {
    background-color: ${p => p.theme.colors.error};
  }
`;

/**
 * @typedef {import('../model/types').Meeting & { assignees: [] }} CardProps
 */
